import { DynamicWithdrawalActivityCTO, PoolDynamicOverviewCTO } from '@shared/sharedTypes'
import React, { useEffect, useState } from 'react'

import { SectionHeader } from '@react/components/common/SectionHeader'
import { useOtChainState, useOtSelector } from '@react/redux/store'
import * as yup from 'yup'

import { Box, Grid, Typography, useTheme } from '@mui/material'
import { AddressPill } from '@react/components/addresses/AddressPill'
import ButtonModal from '@react/components/ButtonModal'
import ViewNumber from '@react/components/elements/ViewNumber'
import AdminDynamicRepayRedeemForm from '@react/dynamic/AdminDynamicRepayRedeemForm'

import { fetchBalance, fetchDynamicPoolWithdrawActivitiesCTOs, scanPoolsForEvents } from '@react/fetchers/PoolFetchers'
import { erc20WeiToCommaDecString, erc20WeiToNumber, otAssert } from '@shared/conversions'

import { RoleEnum, yupErc20WeiRequired } from '@shared/Enums'
import { logOT } from '@shared/loggingShared'

import { ETH_ADDR_ZERO, EthAddr, StringTypeEnum, toFieldMetaData } from '@shared/SchemaTypes'
import { useParams } from 'react-router-dom'
import BorrowerAcceptDynamicRedeemRequestForm from './BorrowerAcceptDynamicRedeemRequestForm'
import AdminDynamicWithdrawDestinationChangeForm from './AdminDynamicWithdrawDestinationChangeForm'
import { JsonRpcProvider } from 'ethers'
import { companyProfilesGet } from '@react/fetchers/companyProfileFetchers'
import { CompanyProfileCTO } from '@shared/CompanyProfileCTO'
import { TableContainer, Table, TableHead, TableBody, TableRow, TableCell } from '@mui/material'

export const DynamicWithdrawalsActionList: React.FC<{ poolAddr?: EthAddr }> = (props) => {
  const {
    stageConfiguration: { restAPIKey, serverApiBaseUrl },
    userConnection: { roleEnum, signer },
    chainConfiguration: { providerReadOnly },
  } = useOtChainState()
  otAssert(providerReadOnly, 'providerReadOnly is not set')
  const poolAddr = (props.poolAddr ? props.poolAddr : useParams().poolAddr) as EthAddr

  const [companyProfiles, setCompanyProfiles] = React.useState<CompanyProfileCTO[]>([])

  const theme = useTheme()

  const poolDynamicOverviewCTO: PoolDynamicOverviewCTO = useOtSelector(
    (state) => state.poolState.poolOverviewCTOs[poolAddr ?? ETH_ADDR_ZERO]
  ) as PoolDynamicOverviewCTO
  const [withdrawActivityCTOs, setWithdrawActivityCTOs] = useState<DynamicWithdrawalActivityCTO[]>([])
  const [totalPendingWithdraws, setTotalPendingWithdraws] = useState<number>(0)
  const [poolBalance, setPoolBalance] = React.useState<BigInt>(0n)
  useEffect(() => {
    if (serverApiBaseUrl) {
      companyProfilesGet(serverApiBaseUrl).then((_companyProfiles) => {
        setCompanyProfiles(_companyProfiles)
      })
    }
  }, [serverApiBaseUrl])
  useEffect(() => {
    async function fetchWithdrawActivities(): Promise<void> {
      if (providerReadOnly && signer && serverApiBaseUrl && restAPIKey && poolAddr) {
        logOT.info(' fetchWithdraws  Start ')
        await scanPoolsForEvents(serverApiBaseUrl, restAPIKey, poolAddr)
        const allPoolActivities = await fetchDynamicPoolWithdrawActivitiesCTOs(serverApiBaseUrl, restAPIKey, poolAddr)

        setWithdrawActivityCTOs(allPoolActivities ?? [])
        logOT.info(' fetchWithdraws Returned requests', allPoolActivities)

        const totalPendingWithdraws = allPoolActivities.reduce((acc, activity) => {
          return acc + erc20WeiToNumber(activity.assets)
        }, 0)
        setTotalPendingWithdraws(totalPendingWithdraws * 1e6)
      }

      const _balance = await fetchBalance(
        poolAddr,
        poolDynamicOverviewCTO.liquidityAssetAddr,
        providerReadOnly as JsonRpcProvider
      )
      setPoolBalance(_balance)
    }

    fetchWithdrawActivities().catch((e) => {
      logOT.info('error', e)
    })
  }, [poolDynamicOverviewCTO])

  if (!poolDynamicOverviewCTO) {
    return <></>
  }

  const topPanelSchema = yup.object({
    totalPendingWithdraws: yupErc20WeiRequired.label('Assets to be Withdrawn').meta(
      toFieldMetaData({
        propName: 'totalPendingWithdraws',
        description: 'The amount of Loan Assets at Maturity that is currently in outstanding Early Withdrawal Requests',
        stringType: StringTypeEnum.STABLE_COINS,
      })
    ),
    poolBalance: yupErc20WeiRequired.label('The amount of Liquidity Assets held by the Pool').meta(
      toFieldMetaData({
        propName: 'poolBalance',
        description: 'The amount of Liquidity Assets held by the Pool',
        stringType: StringTypeEnum.STABLE_COINS,
      })
    ),
  })
  const getCompanyProfile = (userAddr: EthAddr): CompanyProfileCTO => {
    const companyProfile = companyProfiles.find((profile) => profile.wallets.includes(userAddr))

    return (
      companyProfile ?? {
        instructions: '',
        companyName: 'Unlisted',
        uuid: '',
        wallets: [],
        emails: [],
      }
    )
  }
  const liquidityTokenSymbol = poolDynamicOverviewCTO.liquidityTokenSymbol

  return (
    <Box sx={{ paddingTop: 2 }}>
      <SectionHeader>Withdrawal Requests</SectionHeader>
      <Box sx={{ padding: 4, marginBottom: 4 }}>
        <Grid
          container
          spacing={2}
          sx={{ borderBottomWidth: 1, borderBottomColor: theme.palette.divider, marginTop: 2 }}
        >
          <Grid item sx={{ marginBottom: 4 }} xs={12} sm={6}>
            <ViewNumber
              card
              toolTip="The amount of Loan Assets at Maturity that is currently in outstanding Early Withdrawal Requests"
              label="Assets to be Withdrawn"
              inTable={false}
              liquidityTokenSymbol={liquidityTokenSymbol}
              poolTokenSymbol=""
              dataValue={`${totalPendingWithdraws}`}
              fieldSchema={topPanelSchema.describe().fields.totalPendingWithdraws}
              value={`${totalPendingWithdraws}`}
            />
          </Grid>
          <Grid item sx={{ marginBottom: 4 }} xs={12} sm={6}>
            <ViewNumber
              card
              toolTip="The amount of Liquidity Assets held by the Pool"
              label="Pool Balance"
              inTable={false}
              liquidityTokenSymbol={liquidityTokenSymbol}
              poolTokenSymbol=""
              dataValue={`${poolBalance}`}
              fieldSchema={topPanelSchema.describe().fields.poolBalance}
              value={`${poolBalance}`}
            />
          </Grid>
        </Grid>
      </Box>
      <TableContainer sx={{ maxWidth: '100%' }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell width="20%">Lender</TableCell>
              <TableCell width="30%">Request Amount</TableCell>
              <TableCell width="20%">Destination</TableCell>
              <TableCell width="20%">Principal</TableCell>
              <TableCell width="10%" align="center">
                Action
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {withdrawActivityCTOs.map((activity: DynamicWithdrawalActivityCTO) => (
              <TableRow key={activity.uuid}>
                <TableCell>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Typography variant="h6" fontWeight="bold" sx={{ mb: 0.5 }}>
                      {getCompanyProfile(activity.lender).companyName}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <AddressPill addr={activity.lender} />
                    </Box>
                  </Box>
                </TableCell>
                <TableCell>
                  <Typography variant="bodyBold">{erc20WeiToCommaDecString(activity.shares)}</Typography>
                </TableCell>
                <TableCell>
                  {activity.fundsDestination &&
                    getCompanyProfile(activity.fundsDestination).uuid !== getCompanyProfile(activity.lender).uuid && (
                      <Typography color="error">MISS MATCHED COMPANIES</Typography>
                    )}
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    {activity.fundsDestination && (
                      <>
                        <Typography variant="body2" color="text.secondary">
                          {getCompanyProfile(activity.fundsDestination).companyName}
                        </Typography>
                        <AddressPill addr={activity.fundsDestination as string} />
                      </>
                    )}
                    <Typography variant="body2" color="text.secondary">
                      {getCompanyProfile(activity.lender).instructions}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Typography variant="bodyBold">{erc20WeiToCommaDecString(activity.assets)}</Typography>
                </TableCell>
                <TableCell align="right">
                  {activity.stage === 1 && (
                    <ButtonModal
                      title={'Accept Withdraw Request'}
                      variant="primary"
                      disabled={roleEnum !== RoleEnum.BORROWER}
                    >
                      <BorrowerAcceptDynamicRedeemRequestForm
                        activity={activity}
                        symbol={poolDynamicOverviewCTO.symbol}
                        liquidityTokenSymbol={poolDynamicOverviewCTO.liquidityTokenSymbol}
                      />
                    </ButtonModal>
                  )}
                  {activity.stage === 1 && roleEnum === RoleEnum.POOL_ADMIN && (
                    <ButtonModal
                      title={'Change Destination Wallet'}
                      variant="primary"
                      disabled={roleEnum !== RoleEnum.POOL_ADMIN}
                    >
                      <AdminDynamicWithdrawDestinationChangeForm
                        activity={activity}
                        symbol={poolDynamicOverviewCTO.symbol}
                        liquidityTokenSymbol={poolDynamicOverviewCTO.liquidityTokenSymbol}
                      />
                    </ButtonModal>
                  )}
                  {activity.stage === 2 && (
                    <ButtonModal
                      title={'Repay Withdrawal Amount'}
                      variant="primary"
                      disabled={roleEnum !== RoleEnum.POOL_ADMIN}
                    >
                      <AdminDynamicRepayRedeemForm
                        activity={activity}
                        symbol={poolDynamicOverviewCTO.symbol}
                        liquidityTokenSymbol={poolDynamicOverviewCTO.liquidityTokenSymbol}
                      />
                    </ButtonModal>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {withdrawActivityCTOs.length === 0 && (
        <Box sx={{ padding: 4 }}>
          <Typography variant="bodyMuted">No Token transfer Withdrawal Requests at this time.</Typography>
        </Box>
      )}
    </Box>
  )
}
